/* Look at it before you send it.
 *
 * The invoice a customer receives is the only part of an ERP most of their
 * customers ever see, and the commonest way it goes wrong is that nobody
 * looked: the wrong print format, a letterhead that did not load, an address
 * block that ran off the page.
 *
 * So Print opens what will actually be produced, rendered by the same Frappe
 * that makes the PDF, and the download and the email are one click from
 * there. HTML rather than a PDF in an iframe: a PDF viewer in a frame is a
 * plugin, a spinner, and a download nobody asked for.
 */
import { useEffect, useState } from 'react'
import { Loading } from '@jackdesk/shell'
import type { ErpError } from './api'
import { useErp } from './context'

export function PrintDialog({ doctype, name, onClose, onSend, sendWhy, channelsUrl }: {
  doctype: string; name: string; onClose: () => void
  /** The host sends it on the customer's own channels (the desk: WhatsApp or the org's inbox); without
   *  one, the ERP's mailer is offered, which needs an email account on the site. */
  onSend?: () => void
  /** Why the host cannot send it yet (no channel reaches the customer); Send is shown disabled. */
  sendWhy?: string | null
  channelsUrl?: string
}) {
  const erp = useErp()
  const [formats, setFormats] = useState<{ name: string }[]>([])
  const [chosen, setChosen] = useState<string | undefined>()
  const [preview, setPreview] = useState<{ html: string; style: string } | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [busy, setBusy] = useState<'pdf' | 'email' | null>(null)
  const [emailing, setEmailing] = useState(false)
  const [to, setTo] = useState('')

  useEffect(() => {
    erp.printFormats(doctype, name)
      .then((r) => { setFormats(r.formats ?? []); setChosen((c) => c ?? r.default) })
      .catch(() => setFormats([]))
  }, [erp, doctype, name])

  useEffect(() => {
    const controller = new AbortController()
    setPreview(null); setFailed(null)
    erp.printPreview(doctype, name, chosen, controller.signal)
      .then(setPreview)
      .catch((e) => { if ((e as Error).name !== 'AbortError') setFailed((e as ErpError).message) })
    return () => controller.abort()
  }, [erp, doctype, name, chosen])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="palette-wrap" role="dialog" aria-modal="true" aria-label={`Print ${name}`}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="palette" style={{ width: 'min(920px, 96vw)' }}>
        <div className="row" style={{ padding: '10px 14px', gap: 10, borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
          <strong className="small">{name}</strong>
          {formats.length > 1 ? (
            <select className="select" style={{ maxWidth: 220, minHeight: 30 }} aria-label="Print format"
                    value={chosen ?? ''} onChange={(e) => setChosen(e.target.value)}>
              {formats.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
            </select>
          ) : null}
          <span className="grow" />
          <button className="btn btn-sm" disabled={busy !== null} onClick={async () => {
            setBusy('pdf'); setFailed(null)
            try { await erp.downloadPrint(doctype, name, chosen) }
            catch (e) { setFailed((e as ErpError).message) }
            finally { setBusy(null) }
          }}>{busy === 'pdf' ? 'Preparing…' : 'Download PDF'}</button>
          {onSend || sendWhy ? (
            <button className="btn btn-sm" onClick={onSend} disabled={!onSend} title={sendWhy ?? undefined}>
              Send to the customer</button>
          ) : (
            <button className="btn btn-sm" onClick={() => setEmailing((v) => !v)} aria-expanded={emailing}>Email</button>
          )}
          <button className="btn btn-quiet btn-sm" onClick={onClose} aria-label="Close">×</button>
        </div>

        {emailing ? (
          <div className="row" style={{ padding: '10px 14px', gap: 8, borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
            <input className="input" style={{ maxWidth: 320 }} type="email" value={to} placeholder="somebody@example.com"
                   aria-label="Send to" onChange={(e) => setTo(e.target.value)} />
            <button className="btn btn-sm btn-primary" disabled={!to.trim() || busy !== null}
                    onClick={async () => {
                      setBusy('email'); setFailed(null)
                      try {
                        await erp.emailDocument(doctype, name, {
                          recipients: to.trim(), print_format: chosen,
                          subject: `${doctype} ${name}`,
                          content: `<p>Please find ${doctype} ${name} attached.</p>`,
                        })
                        setEmailing(false); setTo('')
                      } catch (e) { setFailed((e as ErpError).message) }
                      finally { setBusy(null) }
                    }}>{busy === 'email' ? 'Sending…' : 'Send'}</button>
            <span className="small muted">The printed copy goes with it.</span>
          </div>
        ) : null}

        {sendWhy ? (
          <div className="small muted" style={{ padding: '8px 14px' }}>
            Not sendable yet: {sendWhy}
            {channelsUrl ? <> <a href={channelsUrl} target="_blank" rel="noreferrer">Connect a channel</a></> : null}
          </div>
        ) : null}
        {failed ? <div className="err" role="alert" style={{ padding: '8px 14px' }}>{failed}</div> : null}

        <div style={{ maxHeight: '68vh', overflow: 'auto', background: 'var(--sunk)', padding: 16 }}>
          {preview === null && !failed ? <Loading rows={6} label="Laying it out" /> : null}
          {preview ? (
            <>
              {/* Frappe's print styles, scoped to the sheet. They are the
                  tenant's own print format, so they are rendered as given. */}
              <style>{preview.style}</style>
              <div style={{ background: '#fff', color: '#000', padding: 24, borderRadius: 4,
                            boxShadow: 'var(--shadow-1)', maxWidth: 820, margin: '0 auto' }}
                   dangerouslySetInnerHTML={{ __html: preview.html }} />
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}
